import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useAuth } from '@/auth/AuthContext';
import { AuthScreen } from '@/auth/AuthScreen';
import { OnboardingShell } from '@/components/Onboarding/OnboardingShell';
import { OnboardingStepAccount } from '@/components/Onboarding/OnboardingStepAccount';
import { OnboardingStepProfile } from '@/components/Onboarding/OnboardingStepProfile';
import { OnboardingStepTerms } from '@/components/Onboarding/OnboardingStepTerms';
import { auth } from '@/firebaseInit';
import { useSubmitPartnerOnboarding } from '@/hooks/useSubmitPartnerOnboarding';
import { inviteCheckMessages, type InviteCheckResultType, type InvitePreviewType } from '@/types/types_invite';
import {
  applyPartnerPrefill,
  makePartnerProfileDraft,
  missingPartnerProfileFields,
  type PartnerProfileDraftType,
} from '@/types/types_partner';
import { callableErrorMessage, invitePreview, redeemPartnerInvite } from '@/util/callable_functions';
import { InviteSignIn } from './InviteSignIn';

/** Whether the link itself is usable — answered by the `invitePreview` call. */
type LinkStateType = 'loading' | 'invalid' | 'valid';

/** A manager's answers to steps 1 and 2, as they are parked between renders. */
interface RegistrationDraftType {
  draft: PartnerProfileDraftType;
  accepted: boolean;
}

/**
 * Where a half-finished manager registration lives. Session, not local, storage:
 * it is scratch space for one sitting, and it holds the partner's contact details,
 * which have no business outliving the tab.
 */
function draftStorageKey(inviteId: string): string {
  return `ed.registration.${inviteId}`;
}

/**
 * Reads back a wizard in progress, or blanks.
 *
 * Signing in is where this flow is most likely to be interrupted — a wrong
 * Google account signs the visitor back out, and a failed sign-in invites a
 * reload — and losing ten filled fields to either would be the worst moment for
 * it.
 *
 * A module-level function so it can seed `useState` directly. Restoring in an
 * effect would mean a render with the fields blank first, which is exactly the
 * flicker this is meant to prevent.
 */
function loadRegistrationDraft(inviteId: string | undefined): RegistrationDraftType {
  const blank: RegistrationDraftType = { draft: makePartnerProfileDraft(), accepted: false };
  if (!inviteId) return blank;
  try {
    const stored = sessionStorage.getItem(draftStorageKey(inviteId));
    if (!stored) return blank;
    const parsed = JSON.parse(stored) as Partial<RegistrationDraftType>;
    return { draft: { ...blank.draft, ...parsed.draft }, accepted: !!parsed.accepted };
  } catch {
    // A corrupt entry is not worth reporting to a partner concierge; start fresh.
    return blank;
  }
}

/**
 * Accepts a partner invitation link: `/invite/:inviteId?t=<token>`.
 *
 * Rendered *outside* the AuthGate (see App.tsx), because the whole point is to
 * be usable by someone who has no account yet. It describes the invite via the
 * unauthenticated `invitePreview` callable, takes the visitor through sign-in,
 * then redeems the link — after which a token refresh pulls in the partner
 * claims the rest of the app checks.
 *
 * The two roles arrive by different doors:
 *
 *   - Staff get this screen as it always was. There is nothing to collect from
 *     them: they cannot edit the partner record, and their partner has already
 *     signed the agreement.
 *   - A manager gets the three-step registration wizard from the
 *     partner-register.html mock — the partner's details, then the NDA and terms,
 *     then their credentials. Their account is the partner's first, so this is the
 *     moment those answers exist to be asked for, and making credentials the
 *     last step means no account is created until they have been given.
 *
 * A manager who somehow gets past this — an account made before the requirement,
 * or a final write that failed — is caught by `PartnerOnboardingGate` instead.
 */
export function PageAcceptInvite() {
  const { inviteId } = useParams<{ inviteId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') ?? '';
  const navigate = useNavigate();

  const { user, partnerUser, signInWithGoogle, signOutUser } = useAuth();

  // A link missing either half is dead on arrival — decided during render, not
  // in an effect, since neither value changes while this page is mounted.
  const malformedLink = !inviteId || !token;

  const [linkState, setLinkState] = useState<LinkStateType>(malformedLink ? 'invalid' : 'loading');
  const [preview, setPreview] = useState<InvitePreviewType | null>(null);
  const [problem, setProblem] = useState<InviteCheckResultType | null>(malformedLink ? 'not-found' : null);
  const [error, setError] = useState<string | null>(null);
  const [redeemed, setRedeemed] = useState(false);
  const [busy, setBusy] = useState(false);

  // Registration wizard, for manager invites only. The two answers are one piece
  // of state because they are stored and restored as one.
  const [step, setStep] = useState(1);
  const [registration, setRegistration] = useState<RegistrationDraftType>(() => loadRegistrationDraft(inviteId));
  const { draft, accepted } = registration;

  // Guard against a second attempt while the first is in flight — the effects
  // below re-run whenever auth state settles, and (for the redeem effect) on
  // every keystroke in the wizard, since the draft is one of its inputs.
  const signingOut = useRef(false);
  const redeeming = useRef(false);

  const isManagerInvite = preview?.role === 'owner';
  const submitOnboarding = useSubmitPartnerOnboarding(preview?.partnerId);

  const invitedEmail = preview?.email ?? '';
  const signedInEmail = (user?.email ?? '').toLowerCase();
  const wrongAccount = !!user && !!invitedEmail && signedInEmail !== invitedEmail;

  // A staff invite has nothing to fill in, so it is ready the moment the page
  // loads; a manager's is ready once steps 1 and 2 are answered.
  const registrationReady = !isManagerInvite || (missingPartnerProfileFields(draft).length === 0 && accepted);

  // Being signed in as the invited person, with the form behind you, *is* the
  // redeeming state — there is nothing left to do, so this is derived.
  const isRedeeming = linkState === 'valid' && !!user && !wrongAccount && registrationReady && !error;

  useEffect(() => {
    if (!inviteId || !token) return;
    let cancelled = false;
    invitePreview({ inviteId, token })
      .then((res) => {
        if (cancelled) return;
        setPreview(res.data);
        if (res.data.result === 'ok') {
          setLinkState('valid');
          // Fills blanks only, so a session restored from storage keeps whatever
          // the manager had already typed over what we think we know.
          const prefill = res.data.partnerPrefill;
          if (prefill) {
            setRegistration((prev) => ({ ...prev, draft: applyPartnerPrefill(prev.draft, prefill) }));
          }
        } else {
          setProblem(res.data.result);
          setLinkState('invalid');
        }
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('invitePreview failed', e);
        setError('Could not check this invitation. Please try again.');
        setLinkState('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [inviteId, token]);

  useEffect(() => {
    if (!inviteId || !isManagerInvite) return;
    sessionStorage.setItem(draftStorageKey(inviteId), JSON.stringify(registration));
  }, [inviteId, isManagerInvite, registration]);

  // Wrong account: sign back out rather than leaving them stuck in a session
  // that cannot accept the invite. Checked regardless of how far through the
  // wizard they are — it blocks the whole thing, so saying so early is kinder
  // than saying so at the end.
  useEffect(() => {
    if (linkState !== 'valid' || !preview || !wrongAccount || signingOut.current) return;
    signingOut.current = true;
    void signOutUser().finally(() => {
      setError(
        `This invitation is for ${invitedEmail}, but you signed in as ${signedInEmail}. Please sign in with ${invitedEmail}.`,
      );
      signingOut.current = false;
    });
  }, [linkState, preview, wrongAccount, invitedEmail, signedInEmail, signOutUser]);

  // Once signed in with the right account and the form behind them, redeem
  // automatically — opening the link, filling it in and signing in is consent.
  useEffect(() => {
    if (linkState !== 'valid' || !user || !preview || !inviteId) return;
    if (wrongAccount || !registrationReady || redeeming.current) return;

    redeeming.current = true;
    void (async () => {
      try {
        await redeemPartnerInvite({ inviteId, token });
        // Pull the freshly-set partner claims into this session; without them
        // the write below, and the first Firestore reads on /partner, would be
        // denied.
        await auth.currentUser?.getIdToken(true);

        if (isManagerInvite) {
          // Deliberately not fatal. The account now exists and works; if this
          // write is what failed, `PartnerOnboardingGate` asks again on the next
          // load rather than stranding them on a page whose sign-in step is
          // already spent.
          try {
            await submitOnboarding(draft, user.uid);
            sessionStorage.removeItem(draftStorageKey(inviteId));
          } catch (e) {
            console.error('Saving registration details failed', e);
          }
        }

        // Navigation waits for AuthProvider's partnerUsers listener (below), so
        // /partner never flashes its "no partner account" state on the way in.
        setRedeemed(true);
      } catch (e) {
        console.error('redeemPartnerInvite failed', e);
        setError(callableErrorMessage(e));
        redeeming.current = false;
      }
    })();
  }, [
    linkState,
    user,
    preview,
    inviteId,
    token,
    wrongAccount,
    registrationReady,
    isManagerInvite,
    draft,
    submitOnboarding,
  ]);

  // Into the portal once the account exists and AuthProvider has seen it. This
  // also covers someone re-opening a link they already used.
  useEffect(() => {
    const alreadyHadAccount = linkState === 'invalid' && problem === 'already-redeemed';
    if (partnerUser && (redeemed || alreadyHadAccount)) {
      navigate('/partner', { replace: true });
    }
  }, [partnerUser, redeemed, linkState, problem, navigate]);

  async function handleGoogle() {
    setError(null);
    setBusy(true);
    try {
      await signInWithGoogle();
    } catch (e) {
      const code = (e as { code?: string }).code;
      if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        console.error(e);
        setError('Sign-in failed. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  const settingUp = (
    <div className='tw:flex tw:flex-col tw:items-center tw:gap-3'>
      <Loader2 className='tw:size-8 tw:animate-spin tw:text-ed-gold' />
      <p className='tw:text-sm tw:text-white/60'>Setting up your account…</p>
    </div>
  );

  const signIn = (
    <InviteSignIn
      partnerName={preview?.partnerName}
      invitedEmail={invitedEmail}
      busy={busy}
      error={error}
      onGoogle={() => void handleGoogle()}
      onError={setError}
    />
  );

  // ----- Manager invite: the three-step registration wizard -----
  if (linkState === 'valid' && isManagerInvite) {
    return (
      <OnboardingShell
        step={step}
        totalSteps={3}
        title='Partner Registration'
        subtitle={`Set up the Eighty Days account for ${preview?.partnerName ?? 'your organisation'}`}
        action={
          user && (
            <button
              type='button'
              onClick={() => void signOutUser()}
              className='tw:text-sm tw:text-white/60 tw:transition-colors tw:hover:text-ed-gold'
            >
              Sign out
            </button>
          )
        }
      >
        {/* Steps 1 and 2 have nowhere of their own to report a problem, and one
            can land on them: arriving already signed in as the wrong person
            signs that session out immediately, which would otherwise happen
            without a word. Step 3 shows its own errors beneath the form. */}
        {error && step !== 3 && <p className='tw:mb-6 tw:text-sm tw:text-ed-red'>{error}</p>}

        {step === 1 && (
          <OnboardingStepProfile
            draft={draft}
            onChange={(patch) => setRegistration((prev) => ({ ...prev, draft: { ...prev.draft, ...patch } }))}
            partnerName={preview?.partnerName ?? ''}
            brand={preview?.partnerPrefill?.brand}
            continueLabel='Continue to Terms'
            onContinue={(next) => {
              if (missingPartnerProfileFields(next).length) return;
              setStep(2);
            }}
          />
        )}

        {step === 2 && (
          <OnboardingStepTerms
            accepted={accepted}
            onAcceptedChange={(next) => setRegistration((prev) => ({ ...prev, accepted: next }))}
            onBack={() => setStep(1)}
            onContinue={() => setStep(3)}
            continueLabel='Continue'
          />
        )}

        {step === 3 &&
          (isRedeeming ? (
            <div className='ed-step-content tw:flex tw:justify-center tw:py-8'>{settingUp}</div>
          ) : (
            <OnboardingStepAccount
              invitedEmail={invitedEmail}
              onBack={() => setStep(2)}
              busy={busy}
              onGoogle={() => void handleGoogle()}
              error={error}
              onError={setError}
            />
          ))}
      </OnboardingShell>
    );
  }

  // ----- Staff invite, and every dead or still-loading link -----
  return (
    <AuthScreen>
      {linkState === 'loading' && <Loader2 className='tw:size-8 tw:animate-spin tw:text-ed-gold' />}

      {linkState === 'invalid' && (
        <div className='tw:max-w-md'>
          <h2 className='tw:font-serif tw:text-xl tw:text-white'>Invitation unavailable</h2>
          <p className='tw:mt-3 tw:text-sm tw:text-white/60'>
            {error ?? (problem ? inviteCheckMessages[problem as Exclude<InviteCheckResultType, 'ok'>] : '')}
          </p>
        </div>
      )}

      {linkState === 'valid' && (isRedeeming ? settingUp : signIn)}
    </AuthScreen>
  );
}
